import { z } from 'zod';
import { isoDateSchema } from './iso-date.ts';
import { moneySchema } from './orders.ts';
import { trafficMetricsSchema } from './traffic.ts';

/**
 * What the Dashboard and Reporting views ask for and receive.
 *
 * One range vocabulary shared by both, so a link from one to the other keeps the
 * period the viewer had selected.
 */

export const rangeKeySchema = z.enum(['today', '7d', '30d', 'custom']);
export type RangeKey = z.infer<typeof rangeKeySchema>;

export const reportQuerySchema = z
  .object({
    range: rangeKeySchema.default('today'),
    from: isoDateSchema.optional(),
    to: isoDateSchema.optional(),
    /** Sparkery staff only. Partners are scoped to themselves by the server. */
    partnerId: z.string().min(1).optional(),
  })
  .refine((q) => q.range !== 'custom' || (q.from && q.to), {
    message: 'a custom range needs both from and to',
    path: ['from'],
  });
export type ReportQuery = z.infer<typeof reportQuerySchema>;

export const barSchema = z.object({
  label: z.string(),
  value: z.number(),
  formatted: z.string(),
});
export type ReportBar = z.infer<typeof barSchema>;

export const orderRowSchema = z.object({
  id: z.string(),
  name: z.string(),
  customerLabel: z.string().nullable(),
  product: z.string(),
  sale: moneySchema,
  commission: moneySchema,
  state: z.enum(['confirmed', 'pending', 'refunded', 'cancelled']),
  occurredAt: z.string(),
});
export type OrderRow = z.infer<typeof orderRowSchema>;

export const salesSummarySchema = z.object({
  /** Sum of order values in the range, store currency. */
  sales: moneySchema,
  orderCount: z.number().int(),
  /**
   * Sum of commission MOVEMENTS — accruals net of any claw-back.
   *
   * Always `payable + forecasted`. The split is the interesting part; the total is kept
   * because it is the figure a partner recognises as "what this period earned me".
   */
  commission: moneySchema,
  /**
   * The part whose 30-day window has closed: a monthly run will settle it, or already
   * has. Called "Payable" throughout the portal — never "pending", which suggested
   * Sparkery was sitting on it.
   */
  payable: moneySchema,
  /**
   * The part still inside its window. Earned on the order, not yet payable, and it can
   * still fall away if the customer refunds — which is exactly why it is labelled
   * "Forecasted" rather than presented as money in hand.
   */
  forecasted: moneySchema,
  /** The rate in force, for the "at 12%" caption. Null for a fixed-fee partner. */
  commissionRate: z.string().nullable(),
  /**
   * Orders as a percentage of sessions, 0–100.
   *
   * **Null when traffic has not synced, never zero.** A rate whose denominator is
   * unknown is not a rate of nothing — and this is the headline figure for a fixed-fee
   * partner, who has no commission to show in its place, so a confident 0.0% would be
   * the most misleading number on their screen.
   *
   * Derived here rather than in the browser so both views state the same figure, and so
   * the null case is decided once by whoever knows whether GA4 answered.
   */
  conversionRate: z.number().nullable(),
});
export type SalesSummary = z.infer<typeof salesSummarySchema>;

export const reportResponseSchema = z.object({
  range: rangeKeySchema,
  from: z.string(),
  to: z.string(),
  /** Null when a viewer sees every partner at once. */
  partnerId: z.string().nullable(),
  partnerName: z.string().nullable(),

  /**
   * Whether this report's subject earns commission at all.
   *
   * Stated by the server rather than inferred from a null rate, because a null rate
   * means two different things: a fixed-fee partner (no commission anywhere in the app)
   * and the whole-programme view (many partners, no single rate). A view that guessed
   * would either hide staff's commission column or show a fixed-fee partner an empty
   * one.
   */
  showsCommission: z.boolean(),

  summary: salesSummarySchema,
  /** Hourly for today, daily otherwise. */
  salesBars: z.array(barSchema),
  visitorBars: z.array(barSchema),
  traffic: trafficMetricsSchema.nullable(),
  orders: z.array(orderRowSchema),

  /**
   * Quality flags, carried all the way to the UI.
   *
   * A figure that is still settling, sampled, or missing its traffic side must say so
   * — a number presented plainly is a number someone will act on.
   */
  quality: z.object({
    provisional: z.boolean(),
    samplingRatio: z.number().nullable(),
    trafficUnavailable: z.boolean(),
  }),
});
export type ReportResponse = z.infer<typeof reportResponseSchema>;
